import { useTheme } from "@/ui/contexts/theme-provider";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface LogoProps {
  className?: string;
  alt?: string;
}

/**
 * Logo component that automatically switches between light and dark theme logos
 * Based on the current theme setting (light, dark, or system preference)
 */
export const Logo = ({ className = "", alt }: LogoProps) => {
  const { t } = useTranslation("common");
  const { theme } = useTheme();
  const [currentTheme, setCurrentTheme] = useState<"light" | "dark">("dark");

  useEffect(() => {
    const root = window.document.documentElement;
    
    // Get the actual applied theme from the root element class
    const getAppliedTheme = (): "light" | "dark" => {
      if (root.classList.contains("dark")) return "dark";
      if (root.classList.contains("light")) return "light";
      // Fallback to system preference
      return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    };
    
    // Set initial theme
    setCurrentTheme(getAppliedTheme());
    
    // If theme is "system", listen for changes
    if (theme === "system") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handleChange = (e: MediaQueryListEvent) => {
        setCurrentTheme(e.matches ? "dark" : "light");
      };
      mediaQuery.addEventListener("change", handleChange);
      
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
    
    // If theme is explicitly set, watch for class changes on root element
    const observer = new MutationObserver(() => {
      setCurrentTheme(getAppliedTheme());
    });
    
    observer.observe(root, {
      attributes: true,
      attributeFilter: ["class"],
    });
    
    return () => observer.disconnect();
  }, [theme]);

  // Determine which logo to use
  // logo-dark.png for dark theme, logo.png for light theme
  const logoPath = currentTheme === "light" 
    ? "/logos/main/logo.png"
    : "/logos/main/logo-dark.png";

  return (
    <img
      src={logoPath}
      alt={alt ?? t("logoAlt")}
      className={className}
    />
  );
};

